import { TFunction } from 'i18next';
import moment from 'moment-timezone';
import { extractSchedulePayload, formatScheduleDisplay, IScheduleDisplay } from 'src/app/Orders/schedule.helpers';
import { GetAppearance } from 'src/utils/CommonFunctions';

export type { IScheduleDisplay } from 'src/app/Orders/schedule.helpers';

interface IScheduleResult {
    display: IScheduleDisplay | null;
    fields: ReturnType<typeof extractSchedulePayload>;
    startDate: string | null;
}

interface ITimeZoneOption {
    value: string;
    label: string;
    subLabel: string;
    country: string;
    abbr: string | null;
}

/**
 * Build the display model for one side of a leg schedule. Delegates to the shared
 * `schedule.helpers` (same source wal-frontend-v2's Radar uses) so the portal renders
 * dates, ranges and local time identically — including the moment.utc parsing that keeps
 * a midnight-UTC PG DATE from shifting a day in western timezones.
 *
 * `userTimezone` is the VIEWER's timezone (appearance), used only for the Local Time row.
 */
export const makeFormatScheduleDisplay = (
    type: 'pickup' | 'delivery',
    schedule: any,
    // IANA zone of the STOP (shipper for pickup, consignee for delivery). The portal's
    // radar payload carries it on the shipper/consignee object, not inside `schedule` —
    // unlike the internal Radar, whose schedule row has pickupTimezone/deliveryTimezone
    // columns. extractSchedulePayload reads it from the schedule, so it must be injected
    // here or Local Time has no source zone to convert FROM and silently stays unconverted.
    sourceTimezone?: string | null,
    dateFormat?: string
): IScheduleResult => {
    if (!schedule) return { display: null, fields: null, startDate: null };

    const extracted = extractSchedulePayload(schedule, type);
    const fields = extracted ? { ...extracted, timezone: extracted.timezone ?? sourceTimezone ?? null } : extracted;

    // Local Time is expressed in the VIEWER's zone (appearance) — the row exists to answer
    // "what time is that for me?", so this is deliberately not the stop's zone.
    const appearance = GetAppearance();
    const viewerTimezone = appearance?.timezone ?? null;

    return {
        display: formatScheduleDisplay(fields, viewerTimezone, dateFormat),
        fields,
        startDate: fields?.startDate ? (fields.startTime ? `${String(fields.startDate).split('T')[0]} ${fields.startTime}` : String(fields.startDate).split('T')[0]) : null,
    };
};

/** "between 09:00 to 16:00" / "at 09:00" — the compact form used inside detail rows. */
export const formatTimeRangeDisplay = (display: IScheduleDisplay | null, t?: (key: string) => string): string => {
    if (!display?.hasData) return '';
    const between = t ? t('Common.between') : 'Between';
    const to = t ? t('Common.to') : 'to';
    const at = t ? t('Common.at') : 'at';
    if (display.hasTimeWindow) return `${between} ${display.timeRange.replace(' - ', ` ${to} `)}`;
    if (display.timeRange !== '-') return `${at} ${display.timeRange}`;

    return '';
};

/**
 * Resolve an IANA zone to its abbreviation (EDT, CST). moment falls back to a numeric
 * offset ("-03", "+0530") when a zone has no alphabetic abbreviation — treat that as
 * "no abbr" so callers render the full zone name instead.
 */
export const getTimeZoneOption = (timezone?: string | null): ITimeZoneOption | undefined => {
    if (!timezone || !moment.tz.zone(timezone)) return undefined;

    const rawAbbr = moment.tz(timezone).zoneAbbr();
    const abbr = /^[+-]\d/.test(rawAbbr) ? null : rawAbbr;
    const tzOffset = moment.tz(timezone).format('Z');
    const country = timezone.split('/')[0];

    return {
        value: timezone,
        label: abbr ? `${timezone} (${abbr})` : timezone,
        subLabel: `UTC${tzOffset}`,
        country,
        abbr,
    };
};

/**
 * Headline window phrase for the card's sub-line:
 *   "Pickup Window (09:00 to 16:00 EDT)" / "Pickup Appointment at 09:00 EDT"
 * `type` switches the Pickup/Delivery wording.
 */
export const formatTimeRangeDisplayNew = (display: IScheduleDisplay | null, t: TFunction, type: 'Pickup' | 'Delivery' = 'Pickup'): string => {
    if (!display?.hasData) return '';

    const zoneLabel = getTimeZoneOption(display?.timezoneLabel)?.abbr ?? display?.timezoneLabel;
    const zoneSuffix = zoneLabel && zoneLabel !== '-' ? ` ${zoneLabel}` : '';

    if (display.hasTimeWindow) {
        return `${t('Radar.Pickup_Window', { type })} (${display.timeRange.replace(' - ', ` ${t('Common.to')} `)}${zoneSuffix})`;
    }

    if (display.timeRange !== '-') {
        return `${t('Radar.Pickup_Appointment', { type })} ${t('Common.at')} ${display.timeRange}${zoneSuffix}`;
    }

    return '';
};
